import {DatePeriodEnum} from '../../../util/date';

export enum ReviewsAndAnswersFilterQueriesEnum {
    PeriodStart = 'raa_start_date',
    PeriodEnd = 'raa_end_date',
    ComparePeriodStart = 'raa_compared_start_date',
    ComparePeriodEnd = 'raa_compared_end_date',
    Period = 'raa_period',
    CompareMode = 'raa_compare_mode',
    // Review filters — names mirror the reviews list `ReviewFilter` (see BST-10425 handoff).
    CatalogIds = 'raa_catalog_ids',
    Sentiments = 'raa_sentiments',
    TagIds = 'raa_tag_ids',
    Rating = 'raa_rating',
    WithoutText = 'raa_without_text',
    IsReplied = 'raa_is_replied',
    ActionReply = 'raa_action_reply',
    Target = 'raa_target',
    IncludeDeleted = 'raa_include_deleted',
}

export type ReviewsAndAnswersFilterQueriesType = Readonly<{
    [ReviewsAndAnswersFilterQueriesEnum.PeriodStart]: string;
    [ReviewsAndAnswersFilterQueriesEnum.PeriodEnd]: string;
    [ReviewsAndAnswersFilterQueriesEnum.ComparePeriodStart]: string;
    [ReviewsAndAnswersFilterQueriesEnum.ComparePeriodEnd]: string;
    [ReviewsAndAnswersFilterQueriesEnum.Period]: DatePeriodEnum | '';
    [ReviewsAndAnswersFilterQueriesEnum.CompareMode]: ReviewsAndAnswersCompareEnum | '';
    [ReviewsAndAnswersFilterQueriesEnum.CatalogIds]: string;
    [ReviewsAndAnswersFilterQueriesEnum.Sentiments]: string;
    [ReviewsAndAnswersFilterQueriesEnum.TagIds]: string;
    [ReviewsAndAnswersFilterQueriesEnum.Rating]: string;
    [ReviewsAndAnswersFilterQueriesEnum.WithoutText]: string;
    [ReviewsAndAnswersFilterQueriesEnum.IsReplied]: string;
    [ReviewsAndAnswersFilterQueriesEnum.ActionReply]: string;
    [ReviewsAndAnswersFilterQueriesEnum.Target]: string;
    [ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted]: string;
}>;

export enum ReviewsAndAnswersCompareEnum {
    PreviousPeriod = 'previous',
    PreviousYear = 'previous_year',
    Custom = 'custom',
}

// Subset of the filter state that maps 1:1 to the backend review filters.
// String values intentionally match the API contract (e.g. without_text='true').
export type ReviewsAndAnswersReviewFiltersType = {
    [ReviewsAndAnswersFilterQueriesEnum.CatalogIds]: Array<number>;
    [ReviewsAndAnswersFilterQueriesEnum.Sentiments]: Array<string>;
    [ReviewsAndAnswersFilterQueriesEnum.TagIds]: Array<number>;
    [ReviewsAndAnswersFilterQueriesEnum.Rating]: Array<number>;
    [ReviewsAndAnswersFilterQueriesEnum.WithoutText]: string;
    [ReviewsAndAnswersFilterQueriesEnum.IsReplied]: string;
    [ReviewsAndAnswersFilterQueriesEnum.ActionReply]: string;
    [ReviewsAndAnswersFilterQueriesEnum.Target]: string;
    // false (default) sends in_trash=false (exclude deleted).
    // true sends in_trash=true, so deleted reviews are counted.
    [ReviewsAndAnswersFilterQueriesEnum.IncludeDeleted]: boolean;
};

export type ReviewsAndAnswersFilterStateType = Readonly<
    {
        [ReviewsAndAnswersFilterQueriesEnum.PeriodStart]: Date;
        [ReviewsAndAnswersFilterQueriesEnum.PeriodEnd]: Date;
        [ReviewsAndAnswersFilterQueriesEnum.ComparePeriodStart]: Date | null;
        [ReviewsAndAnswersFilterQueriesEnum.ComparePeriodEnd]: Date | null;
        [ReviewsAndAnswersFilterQueriesEnum.Period]: DatePeriodEnum;
        [ReviewsAndAnswersFilterQueriesEnum.CompareMode]: ReviewsAndAnswersCompareEnum | null;
    } & ReviewsAndAnswersReviewFiltersType
>;

export type ReviewsAndAnswersFilterDispatcherType = (action: ReviewsAndAnswersFilterUpdateActionType) => void;

export type ReviewsAndAnswersFilterHookType = {
    filter: ReviewsAndAnswersFilterStateType;
    dispatchFilter: ReviewsAndAnswersFilterDispatcherType;
    isCompareMode: boolean;
    lastAvailableAnalyticsDay: Date;
};

export enum ReviewsAndAnswersFilterActionsEnum {
    UpdateMainPeriod,
    UpdateComparePeriod,
    UpdatePeriod,
    UpdateCompareMode,
    UpdateReviewFilters,
    ResetReviewFilters,
    Init,
}

export type ReviewsAndAnswersFilterUpdateActionType =
    | {
          type: ReviewsAndAnswersFilterActionsEnum.UpdateCompareMode;
          payload: ReviewsAndAnswersFilterStateType[ReviewsAndAnswersFilterQueriesEnum.CompareMode];
      }
    | {
          type: ReviewsAndAnswersFilterActionsEnum.UpdateMainPeriod;
          payload: Pick<
              ReviewsAndAnswersFilterStateType,
              ReviewsAndAnswersFilterQueriesEnum.PeriodStart | ReviewsAndAnswersFilterQueriesEnum.PeriodEnd
          >;
      }
    | {
          type: ReviewsAndAnswersFilterActionsEnum.UpdateComparePeriod;
          payload: Pick<
              ReviewsAndAnswersFilterStateType,
              | ReviewsAndAnswersFilterQueriesEnum.ComparePeriodStart
              | ReviewsAndAnswersFilterQueriesEnum.ComparePeriodEnd
          > & {
              [ReviewsAndAnswersFilterQueriesEnum.CompareMode]?: ReviewsAndAnswersFilterStateType[ReviewsAndAnswersFilterQueriesEnum.CompareMode];
          };
      }
    | {
          type: ReviewsAndAnswersFilterActionsEnum.UpdatePeriod;
          payload: ReviewsAndAnswersFilterStateType[ReviewsAndAnswersFilterQueriesEnum.Period];
      }
    | {
          type: ReviewsAndAnswersFilterActionsEnum.UpdateReviewFilters;
          payload: Partial<ReviewsAndAnswersReviewFiltersType>;
      }
    | {
          type: ReviewsAndAnswersFilterActionsEnum.ResetReviewFilters;
      }
    | {
          type: ReviewsAndAnswersFilterActionsEnum.Init;
      };
